'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import SearchBar from '@/components/shared/SearchBar';
import FilterRow from '@/components/shared/FilterRow';
import Badge from '@/components/shared/Badge';
import EmptyState from '@/components/shared/EmptyState';
import { MarketplaceCardSkeleton } from '@/components/shared/Skeletons';
import { mockListings } from '@/data/mockMarketplace';
import { ShoppingBag, Archive, Car, Bike, Search, Lock, ArrowRight } from 'lucide-react';

const categoryFilters = [
  { label: 'All', value: 'all' },
  { label: 'Furniture', value: 'Furniture' },
  { label: 'Electronics', value: 'Electronics' },
  { label: 'Home Decor', value: 'Home Decor' },
  { label: 'Baby Items', value: 'Baby Items' },
  { label: 'Fitness Equipment', value: 'Fitness Equipment' },
  { label: 'Office', value: 'Office' },
  { label: 'Tickets / Misc', value: 'Tickets / Misc' },
  { label: 'Storage', value: 'Storage' },
  { label: 'Parking', value: 'Parking' },
  { label: 'Bicycles', value: 'Bicycles' },
  { label: 'Wanted', value: 'Wanted' },
];

const statusFilters = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Available', value: 'Available' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Sold', value: 'Sold' },
];

const categoryIconMap: Record<string, React.ReactNode> = {
  Storage: <Archive size={24} style={{ color: 'rgba(167,163,196,0.20)', margin: '0 auto 6px' }} />,
  Parking: <Car size={24} style={{ color: 'rgba(167,163,196,0.20)', margin: '0 auto 6px' }} />,
  Bicycles: <Bike size={24} style={{ color: 'rgba(167,163,196,0.20)', margin: '0 auto 6px' }} />,
  Wanted: <Search size={24} style={{ color: 'rgba(167,163,196,0.20)', margin: '0 auto 6px' }} />,
};

function getCategoryIcon(category: string) {
  return categoryIconMap[category] || <ShoppingBag size={28} style={{ color: 'rgba(167,163,196,0.20)', margin: '0 auto 6px' }} />;
}

function getCategoryColor(category: string): string {
  const map: Record<string, string> = {
    Storage: '#F0A832',
    Parking: '#2DD4A0',
    Bicycles: '#4CC9FF',
    Wanted: '#9B7FFF',
  };
  return map[category] || 'rgba(167,163,196,0.25)';
}

function getStatusBadge(status: string) {
  if (status === 'Available') return 'available' as const;
  if (status === 'Pending') return 'pendingSale' as const;
  return 'sold' as const;
}

function AvatarInitials({ name }: { name: string }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[9px]"
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.20)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

export default function MarketplacePage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [isLoading] = useState(false);

  const filtered = mockListings.filter((l) => {
    const matchesSearch =
      !search ||
      l.title.toLowerCase().includes(search.toLowerCase()) ||
      l.category.toLowerCase().includes(search.toLowerCase()) ||
      l.sellerName.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'all' || l.category === activeCategory;
    const matchesStatus = activeStatus === 'all' || l.status === activeStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const isFiltered = search || activeCategory !== 'all' || activeStatus !== 'all';

  // Mock: not approved — show teaser state
  const isApproved = false;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Marketplace"
          title="Resident Marketplace"
          description="Buy, sell, and find what you need with your neighbors. All listings are from verified Panorama residents."
          stat={{ value: `${mockListings.filter((l) => l.status === 'Available').length}`, label: 'Active listings' }}
        />

        {/* Private launch teaser banner */}
        {!isApproved && (
          <div
            className="border-b"
            style={{ background: 'rgba(122,92,255,0.04)', borderColor: 'rgba(122,92,255,0.15)' }}
          >
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock size={14} style={{ color: '#9B7FFF', flexShrink: 0 }} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  <span style={{ color: '#F2F4FF' }}>Resident Marketplace</span> is part of the private Panorama network. Access is rolling out in waves for verified residents.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href="/request-access" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all duration-150" style={{ background: 'rgba(122,92,255,0.15)', border: '1px solid rgba(122,92,255,0.30)', color: '#C4BEFF' }}>
                  Request Access <ArrowRight size={11} />
                </Link>
                <Link href="/login" className="text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all duration-150" style={{ color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 max-w-sm">
              <SearchBar placeholder="Search listings, categories..." value={search} onChange={setSearch} />
            </div>
          </div>
          <div className="flex flex-col gap-3 mb-8">
            <FilterRow filters={categoryFilters} active={activeCategory} onChange={setActiveCategory} />
            <FilterRow filters={statusFilters} active={activeStatus} onChange={setActiveStatus} />
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <MarketplaceCardSkeleton key={i} />)}
            </div>
          )}

          {!isLoading && filtered.length === 0 && (
            <EmptyState
              title={isFiltered ? 'No listings match your search' : 'No listings yet'}
              description={isFiltered ? 'Try adjusting your search or filters.' : 'Be the first to post a listing in this category.'}
              icon={<ShoppingBag size={22} style={{ color: '#4CC9FF' }} />}
              action={
                isFiltered ? (
                  <button onClick={() => { setSearch(''); setActiveCategory('all'); setActiveStatus('all'); }} className="btn-secondary text-[13px] px-4 py-2">
                    Clear filters
                  </button>
                ) : undefined
              }
            />
          )}

          {!isLoading && filtered.length > 0 && (
            <>
              <p className="text-[13px] mb-5" style={{ color: '#A7A3C4' }}>
                {filtered.length} listing{filtered.length !== 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filtered.map((listing) => (
                  <Link key={listing.id} href={`/marketplace/${listing.slug}`} className="block group focus-visible:outline-none">
                    <div className="neon-card overflow-hidden cursor-pointer h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
                      {/* Image placeholder */}
                      <div
                        className="h-44 flex items-center justify-center"
                        style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                      >
                        <div className="text-center">
                          {getCategoryIcon(listing.category)}
                          <span className="text-[11px]" style={{ color: 'rgba(167,163,196,0.25)' }}>{listing.category}</span>
                        </div>
                      </div>

                      <div className="p-4">
                        {/* Category tag for new types */}
                        {['Storage', 'Parking', 'Bicycles', 'Wanted'].includes(listing.category) && (
                          <div className="mb-2">
                            <span
                              className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide"
                              style={{
                                background: `${getCategoryColor(listing.category)}12`,
                                color: getCategoryColor(listing.category),
                                border: `1px solid ${getCategoryColor(listing.category)}22`,
                              }}
                            >
                              {listing.category}
                            </span>
                          </div>
                        )}

                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-display font-semibold text-[14px] leading-snug line-clamp-2" style={{ color: '#F2F4FF' }}>
                            {listing.title}
                          </h3>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <span className="font-display font-bold text-[17px]" style={{ color: '#F2F4FF' }}>
                            {listing.priceDisplay}
                          </span>
                          <Badge variant={getStatusBadge(listing.status)} label={listing.status} />
                        </div>

                        <div className="flex items-center gap-2 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <AvatarInitials name={listing.sellerName} />
                          <div className="min-w-0">
                            <p className="text-[12px] font-medium truncate" style={{ color: 'rgba(242,244,255,0.70)' }}>
                              {listing.sellerName}
                            </p>
                            <p className="text-[11px]" style={{ color: 'rgba(167,163,196,0.50)' }}>
                              {listing.sellerTower}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* Cross-links */}
          <div
            className="mt-16 pt-8 border-t"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-4" style={{ color: 'rgba(167,163,196,0.35)' }}>
              Also in the community
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/directory" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Resident Directory
              </Link>
              <Link href="/services" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Resident Services
              </Link>
              <Link href="/conversations" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Conversations
              </Link>
              <Link href="/events" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Events
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
